<template>
    <simple-modal :show="show" :show-title="false"
                  @close="closeModal()">
        <div>
            <div slot="title" class="pb-4">
                <div class="flex justify-between items-center">
                    <h3 class="text-lg font-medium text-gray-900">
                        {{ showPortalContent ? "Edit Communication Settings" : (tenant ? "" : "Bulk ") + "Share Maintenance Request Link" }}
                    </h3>
                    <button :disabled="showPortalContent || form.busy" v-if="!showPortalContent"
                            class="text-gray-800 font-semibold outline-none hover:text-indigo-600 transition duration-300"
                            @click="closeModal()" type="button">&#10005;
                    </button>
                </div>
            </div>

            <!-- Notification Settings -->
            <portal-target name="savedReplyNotificationInputPortal" v-show="showPortalContent" @close="closeModal()"></portal-target>
            <div v-show="!showPortalContent">
                <div v-if="!loadingCommunicationScenarios">
                    <div class="flex flex-col space-y-4 py-2">
                        <span v-if="!tenant && !this.form.errors.has('bulk_errors')" class="text-gray-400 text-md font-normal flex justify-center">
                            Notifying the primary {{ "tenant" | pluralizeOnlyS(selectedAssetIds.length) }} of {{ selectedAssetIds.length }} {{ "asset" | pluralizeOnlyS(selectedAssetIds.length) }}
                        </span>

                        <div class="mb-2 flex flex-wrap items-center justify-between">
                            <div class="font-medium text-md mb-1">Communication Scenario</div>
                            <simple-dropdown :label="selectedCommunicationScenario ? selectedCommunicationScenario.name : 'Choose Scenario' | titlecase"
                                             class="w-full sm:w-1/2">
                                <template slot="buttonOptions">
                                    <simple-dropdown-option v-for="scenario in communicationScenarios" class="flex items-center"
                                                            :key="scenario.id + 'scenario'" :value="scenario"
                                                            @click="selectCommunicationScenario(scenario)">
                                        {{ scenario.name | titlecase }}
                                        <icon-chip v-if="scenario.is_system" class="pl-1 text-gray-400 h-2 w-2"></icon-chip>
                                    </simple-dropdown-option>
                                </template>
                            </simple-dropdown>
                        </div>

                        <ul class="space-y-4 overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100 mt-2">
                            <li class="flex flex-col space-y-4 p-4 bg-indigo-50 rounded-md">
                                <!-- Tenant Details -->
                                <div v-if="tenant" class="flex flex-col md:flex-row md:justify-between">
                                    <div class="flex flex-col order-2 md:order-1">
                                        <div class="mr-1 font-normal flex items-center">
                                            {{ tenant.full_name ? tenant.full_name : tenant.name }}
                                        </div>

                                        <div class="flex flex-col md:flex-row md:items-center text-indigo-600 text-xs font-normal">
                                            <span v-if="tenant.email" :class="{'text-red-500' : disableTenantEmailOption}"> {{ tenant.email }} </span>
                                            <span v-if="tenant.email && tenant.mobile && notSmallScreen" class="px-2"> &middot;</span>
                                            <span v-if="tenant.mobile" :class="{'text-red-500' : disableTenantSMSOption}"> {{ tenant.mobile }} </span>
                                        </div>
                                    </div>

                                    <div class="font-medium md:font-normal order-1 md:order-2 flex text-md md:text-sm justify-center mb-1 md:mb-0 md:justify-end">
                                        Primary Tenant
                                    </div>
                                </div>

                                <div class="flex flex-col space-y-2">
                                    <div>
                                        <saved-reply-notification-input type="email" role="tenant" @input="clearErrors('tenant', 'email')"
                                                                        :to-email="null" category="maintenance requests" disable-ccs disable-attachments
                                                                        edit-message="Please note that the Saved Reply tags will be replaced during sending of the maintenance request link"
                                                                        v-model="form.tenant_email_communication" @toggle-edit-portal="toggleEditPortal()"
                                                                        v-if="(tenant && tenant.email) || !tenant">
                                        </saved-reply-notification-input>

                                        <span v-else class="font-medium">Primary tenant does not have a valid email address</span>

                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`tenant_email_communication.custom_body`)">
                                            {{ form.errors.get(`tenant_email_communication.custom_body`) }}
                                        </span>
                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`tenant_email_communication.media`)">
                                            {{ form.errors.get(`tenant_email_communication.media`) }}
                                        </span>
                                    </div>

                                    <div>
                                        <saved-reply-notification-input type="sms" role="tenant" @input="clearErrors('tenant', 'sms')"
                                                                        category="maintenance requests" disable-ccs disable-attachments
                                                                        edit-message="Please note that the Saved Reply tags will be replaced during sending of the maintenance request link"
                                                                        v-model="form.tenant_sms_communication" @toggle-edit-portal="toggleEditPortal()"
                                                                        v-if="(tenant && tenant.mobile) || !tenant">
                                        </saved-reply-notification-input>

                                        <span v-else class="font-medium">Primary tenant does not have a valid mobile number</span>

                                        <span class="text-red-500 text-sm mt-1 flex justify-end"
                                              v-if="form.errors.has(`tenant_sms_communication.custom_body`)">
                                            {{ form.errors.get(`tenant_sms_communication.custom_body`) }}
                                        </span>
                                    </div>

                                    <span class="text-red-500 text-sm mt-1 flex justify-end"
                                          v-if="form.errors.has('any_communication_selected')">
                                        {{ form.errors.get("any_communication_selected") }}
                                    </span>

                                    <span
                                        class="text-yellow-600 font-normal mt-1 overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-200"
                                        style="max-height: 10rem"
                                        v-html="form.errors.get('bulk_errors')"
                                        v-if="form.errors.has('bulk_errors')">
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <!-- Ticket Close Actions -->
                    <div class="flex space-x-2 mt-2 flex-wrap items-center justify-end">
                        <secondary-button :disabled="form.busy || (tenant && !tenant.mobile && !tenant.email || this.form.errors.has('bulk_errors'))"
                                          @click.prevent="shareLink()"
                                          :name="form.busy ? 'Sharing Link...' : 'Share Link'">
                        </secondary-button>
                    </div>
                </div>

                <div v-else-if="loadingCommunicationScenarios" class="flex justify-center my-auto py-24">
                    <icon-refresh class="h-10 w-10 text-indigo-600 animate-spin"/>
                </div>
            </div>
        </div>
    </simple-modal>

</template>

<script>
import CommunicationScenarioMixin from "../../mixins/communication-scenario-mixin";
import ContactCommunicationMixin from "../../mixins/contact-communication-mixin";

export default {
    name: "ShareMaintenanceRequestLinkModal",

    mixins: [CommunicationScenarioMixin, ContactCommunicationMixin],

    props: {
        show: {
            required: true,
            type: Boolean,
        },
        asset: {
            required: false,
            type: Object,
            description: "An asset used to populate saved reply tags"
        },
        selectedAssetIds: {
            required: false,
            type: Array,
            description: "Used when bulk sharing"
        },
        tenant: {
            required: false,
            type: Object,
            description: "A tenant used to send notifications to when necessary"
        },
    },

    data () {
        return {
            form: new AppForm({
                resolved: false,
                any_communication_selected: false,
                tenant_email_communication: null,
                tenant_sms_communication: null,
            }),
            showPortalContent: false,
            communicationScenarios: [],
            loadingCommunicationScenarios: true,
        };
    },

    computed: {
        disableTenantEmailOption () {
            return this.validateContactEmail(this.tenant);
        },
        disableTenantSMSOption () {
            return this.tenant == null || this.tenant.mobile == null;
        },
    },

    mounted () {
        this.getCommunicationScenarios(["sharing maintenance request link"])
        .then(() => this.loadingCommunicationScenarios = false);
    },

    methods: {
        shareLink () {
            this.form.any_communication_selected = !_.isEmpty(this.form.tenant_email_communication) || !_.isEmpty(this.form.tenant_sms_communication);

            if (this.tenant) {
                this.shareLinkToSingleTenant();
            } else {
                this.bulkShare();
            }
        },

        shareLinkToSingleTenant () {
            this.form.asset_id = this.asset?.id;
            this.form.tenant_id = this.tenant?.id;

            App.post("/maintenance/requests/share/share-link", this.form)
            .then((response) => {
                toastr.success(response);
                this.closeModal();
            }).catch((error) => {
                toastr.error("Could not send maintenance request link. Error: " + error.message);
            });
        },

        bulkShare () {
            this.form.selected_asset_ids = this.selectedAssetIds;

            App.post("/maintenance/requests/share/bulk-share-link", this.form)
            .then((response) => {
                toastr.success(response.message);
                this.clearSelection();

                if (response.errorMessage) {
                    this.form.setErrors({bulk_errors: [response.errorMessage]});
                } else {
                    this.closeModal();
                }
            }).catch((error) => {
                toastr.error("Could not send maintenance request link in bulk. Error: " + error.message);
            });
        },

        selectCommunicationScenario (scenario) {
            this.selectedCommunicationScenario = scenario;

            if (this.selectedCommunicationScenario && this.selectedCommunicationScenario.saved_replies) {
                this.defaultCommunication = this.getSavedReplies(scenario);
                // reset v-model
                this.form.tenant_email_communication = {refresh: true};
                this.form.tenant_sms_communication = {refresh: true};

                this.$nextTick(function () {
                    this.form.tenant_email_communication = this.tenant && this.disableTenantEmailOption ? null : _.cloneDeep(this.defaultCommunication.tenant_email);
                    this.form.tenant_sms_communication = this.tenant && this.disableTenantSMSOption ? null : _.cloneDeep(this.defaultCommunication.tenant_sms);
                });
            }
        },

        clearErrors (role, commType) {
            if (this.form.errors.has("any_communication_selected")) {
                this.form.errors.forget("any_communication_selected");
            }
            if (this.form.errors.has(`${role}_${commType}_communication.custom_body`)) {
                this.form.errors.forget(`${role}_${commType}_communication.custom_body`);
            }
        },

        toggleEditPortal () {
            this.showPortalContent = !this.showPortalContent;
        },

        closeModal () {
            if (this.tenant) {
                this.clearSelection();
            }

            if (this.form.errors.has("bulk_errors")) {
                this.form.errors.forget("bulk_errors");
            }

            this.$emit("toggle-modal");
        },

        clearSelection () {
            this.$emit("clear-selection");
        },
    },
};
</script>
